import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, X, AlertCircle, ChevronDown, ChevronRight,
  Search, Filter, Clock, Calendar, Bot, ArrowDown, ArrowUp,
  User, FileText, Share2, Shield, Camera, Eye, ExternalLink
} from "lucide-react";
import { useSharedEvents } from '../TIMEBAR/SharedEventsContext';
import { useAppStore } from '../../store/appStore';
import './SharedSpace.css';

const SharedSpace = () => {
  const [expandedEvents, setExpandedEvents] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState('desc');
  const [robotFilter, setRobotFilter] = useState(null);
  const [activePriority, setActivePriority] = useState(null);
  const [responseMode, setResponseMode] = useState(false);
  const [currentResponse, setCurrentResponse] = useState("");
  const [currentResponseId, setCurrentResponseId] = useState(null);
  
  // Get shared events from context
  const { 
    sharedEvents, 
    removeSharedEvent, 
    addMessageToEvent, 
    updateSharedEvent,
    changeEventStatus
  } = useSharedEvents();
  
  // Get the updateVideoState function from app store
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // Action required labels
  const actionRequiredLabels = {
    "dispatch": "Immediate Dispatch",
    "investigate": "Investigate ASAP",
    "monitor": "Monitor & Update",
    "reference": "For Reference Only"
  };
  
  // Items per page for pagination
  const ITEMS_PER_PAGE = 5;
  
  // Filter events based on criteria - IMPORTANT: only show events saved to 'shared'
  const filteredEvents = sharedEvents.filter(event => {
    // Only show events that are explicitly marked as shared
    if (!Array.isArray(event.savedTo)) {
      event.savedTo = [event.savedTo || 'workspace'];
    }
    if (!event.savedTo.includes('shared')) {
      return false;
    }
    
    // Filter by active filter type
    if (activeFilter !== 'all') {
      if (activeFilter === 'dispatch' && event.actionRequired !== 'dispatch') {
        return false;
      } else if (activeFilter === 'investigate' && event.actionRequired !== 'investigate') {
        return false;
      } else if (activeFilter === 'monitor' && event.actionRequired !== 'monitor') {
        return false;
      } else if (activeFilter === 'reference' && event.actionRequired !== 'reference') {
        return false;
      }
    }
    
    // Filter by robot ID
    if (robotFilter && event.robotId !== robotFilter) {
      return false;
    }
    
    // Filter by priority
    if (activePriority && event.priority !== activePriority) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const matchesEvent = event.event && event.event.toLowerCase().includes(lowerSearchTerm);
      const matchesMessages = event.messages && event.messages.some(msg => 
        msg.message && msg.message.toLowerCase().includes(lowerSearchTerm)
      );
      
      return matchesEvent || matchesMessages;
    }
    
    return true;
  });
  
  // Sort filtered events
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    // Get timestamps for sorting
    const getTimestamp = (event) => {
      if (event.timestamp) return new Date(event.timestamp).getTime();
      return event.startTime || 0;
    };
    
    const aTime = getTimestamp(a);
    const bTime = getTimestamp(b);
    
    return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
  });
  
  // Paginate events
  const paginatedEvents = sortedEvents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
  
  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };
  
  // Toggle event expansion
  const toggleEventExpand = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };
  
  // Remove event
  const handleRemoveEvent = (id, robotId) => {
    if (window.confirm("Are you sure you want to remove this shared event?")) {
      removeSharedEvent(id, robotId);
    }
  };
  
  // Set robot filter
  const handleRobotFilter = (robotId) => {
    setRobotFilter(prev => prev === robotId ? null : robotId);
    setCurrentPage(1);
  };
  
  // Set priority filter
  const handlePriorityFilter = (priority) => {
    setActivePriority(prev => prev === priority ? null : priority);
    setCurrentPage(1);
  };
  
  // Update event status
  const updateEventStatus = (eventId, status) => {
    if (!eventId) return;
    
    changeEventStatus(eventId, status);
    
    // Alternatively, if changeEventStatus is not available, use updateSharedEvent:
    // updateSharedEvent(eventId, { status });
  };
  
  // Format time display
  const formatTime = (timestamp) => {
    if (!timestamp) return "Unknown time";
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return timestamp;
    }
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString();
    } catch (e) {
      return "";
    }
  };
  
  // Enable response mode
  const handleRespond = (eventId) => {
    setResponseMode(true);
    setCurrentResponseId(eventId);
    setCurrentResponse("");
  };
  
  // Send response
  const sendResponse = () => {
    if (!currentResponse.trim() || !currentResponseId) return;
    
    // Add message to event using context method
    addMessageToEvent(currentResponseId, currentResponse);
    
    // Reset response mode
    setResponseMode(false);
    setCurrentResponseId(null);
    setCurrentResponse("");
    
    // Expand the event to show the new message
    setExpandedEvents(prev => ({
      ...prev,
      [currentResponseId]: true
    }));
  };
  
  // Cancel response
  const cancelResponse = () => {
    setResponseMode(false);
    setCurrentResponseId(null);
    setCurrentResponse("");
  };
  
  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return "#ef4444"; // Emergency - Red
      case 2: return "#f97316"; // Urgent - Orange
      case 3: return "#eab308"; // Moderate - Yellow
      case 4: return "#22c55e"; // Advisory - Green
      default: return "#9ca3af"; // Default - Gray
    }
  };
  
  // Get priority label
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return "Emergency";
      case 2: return "Urgent";
      case 3: return "Moderate";
      case 4: return "Advisory";
      default: return "Unspecified";
    }
  };
  
  // Get event type label
  const getEventTypeLabel = (type) => {
    switch (Number(type)) {
      case 1: return "Civil-Vehicle";
      case 2: return "Civil-Person";
      case 3: return "Civil-Other";
      case 4: return "Crime-Vehicle";
      case 5: return "Crime-Person";
      case 6: return "Crime-Other";
      default: return "Event";
    }
  };

  // Handler for "Check Details" button
  const handleGoDetailsClick = (event, e) => {
    e.stopPropagation(); // Prevent the card click event from triggering
    
    // Generate timePart based on timeframe

// Generate timePart based on timeframe
// Generate timePart based on event contents - more reliable determination
let timePart = "day"; // Default to night
if (event.timeframe === "11PM" || 
    (event.event && event.event.toLowerCase().includes('night')) || 
    (event.shareMessage && event.shareMessage.toLowerCase().includes('night'))) {
  timePart = "night";
}

    const videoUrl = `/${timePart}/videos/video${event.robotId}.mp4`;
    
    console.log("Go Details clicked for event:", event);
    
    // Update the video state
    try {
      updateVideoState({
        robotId: event.robotId,
        eventId: null, // We don't have an event index here
        videoUrl,
        time: event.startTime,
        isPlaying: true,
        isHls: false,
        hlsResumeTime: null,
        hlsBaseTime: 0,
        currentEvent: event
      }, 'shared_space_go_details');
      
      console.log(`Go Details: Video state updated, jumping to time: ${event.startTime}s`);
    } catch (err) {
      console.error(`Go Details: Error updating video state:`, err);
    }
  };

  return (
    <div className="shared-space">
      <div className="shared-space-header">
        
        <div className="shared-space-controls">
          <div className="filter-buttons-container">
            <button 
              className={`shared-space-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              <Filter size={14} />
              <span>All</span>
            </button>
            
            <button 
              className={`shared-space-filter-btn ${activeFilter === 'dispatch' ? 'active' : ''}`}
              onClick={() => setActiveFilter('dispatch')}
            >
              <AlertCircle size={14} />
              <span>Dispatch</span>
            </button>
            
            <button 
              className={`shared-space-filter-btn ${activeFilter === 'investigate' ? 'active' : ''}`}
              onClick={() => setActiveFilter('investigate')}
            >
              <Clock size={14} />
              <span>Investigate</span>
            </button>
            
            <button 
              className={`shared-space-filter-btn ${activeFilter === 'monitor' ? 'active' : ''}`}
              onClick={() => setActiveFilter('monitor')}
            >
              <Eye size={14} />
              <span>Monitor</span>
            </button>
            
            <button 
              className={`shared-space-filter-btn ${activeFilter === 'reference' ? 'active' : ''}`}
              onClick={() => setActiveFilter('reference')}
            >
              <FileText size={14} />
              <span>Reference</span>
            </button>
          </div>
        </div>
      </div>
      
      {responseMode && (
        <div className="response-panel">
          <div className="response-panel-header">
            <h3>Respond to Shared Event</h3>
            <button className="response-panel-close" onClick={cancelResponse}>
              <X size={16} />
            </button>
          </div>
          <div className="response-panel-content">
            <textarea
              className="response-input"
              value={currentResponse}
              onChange={(e) => setCurrentResponse(e.target.value)}
              placeholder="Type your response or update here..."
              rows={4}
            />
          </div>
          <div className="response-panel-footer">
            <button className="response-btn cancel" onClick={cancelResponse}>Cancel</button>
            <button 
              className="response-btn send"
              onClick={sendResponse}
              disabled={!currentResponse.trim()}
            >
              <MessageSquare size={14} />
              Send Response
            </button>
          </div>
        </div>
      )}
      
      {paginatedEvents.length === 0 ? (
        <div className="shared-space-empty">
          {searchTerm || activeFilter !== 'all' || robotFilter || activePriority ? (
            <>
              <AlertCircle size={40} />
              <p>No matching shared events found.</p>
              <p className="shared-space-empty-hint">Try adjusting your filters or search terms.</p>
            </>
          ) : (
            <>
              <Share2 size={40} />
              <p>No shared events yet</p>
              <p className="shared-space-empty-hint">
                Team members will share important events here for collaboration
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="shared-space-list">
          {paginatedEvents.map((event) => {
            const eventId = event.id || `${event.robotId}-${event.timeframe}-${event.startTime}`;
            const isExpanded = expandedEvents[eventId];
            const hasMessages = event.messages && event.messages.length > 0;
            const eventPriority = event.priority || 3;
            const eventStatus = event.status || 'pending';
            
            return (
              <div 
                key={eventId}
                className={`shared-event-card priority-${eventPriority} status-${eventStatus}`}
              >
                <div className="shared-event-header">
                  {/* 将展开/折叠按钮移到最左侧 */}
                  <button 
                    className="shared-event-toggle"
                    onClick={() => toggleEventExpand(eventId)}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  
                  <div className="shared-event-title-area">
                    <div 
                      className="shared-event-priority-indicator"
                      style={{ backgroundColor: getPriorityColor(eventPriority) }}
                      title={getPriorityLabel(eventPriority)}
                    ></div>
                    
                    <div className="shared-event-info">
                      <div className="shared-event-meta">
                        <span className="shared-event-time">
                          <Clock size={12} />
                          {formatTime(event.timestamp)}
                        </span>
                        
                        <span className="shared-event-date">
                          <Calendar size={12} />
                          {formatDate(event.timestamp)}
                        </span>
                        
                        {hasMessages && (
                          <span className="shared-event-messages-count">
                            <MessageSquare size={12} />
                            {event.messages.length}
                          </span>
                        )}
                        
                        {event.robotId && (
                          <span className="shared-event-robot">
                            <Camera size={12} />
                            <span>Robot {event.robotId}</span>
                          </span>
                        )}
                        
                        <span className="shared-event-type">
                          {getEventTypeLabel(event.type)}
                        </span>
                        
                        {/* 添加行动要求标签 */}
                        {event.actionRequired && (
                          <span className={`shared-event-action-required action-${event.actionRequired}`}>
                            <AlertCircle size={12} />
                            <span>{actionRequiredLabels[event.actionRequired] || event.actionRequired}</span>
                          </span>
                        )}
                      </div>
                      
                      <h3 className="shared-event-title">
                        {event.event || "Unnamed Event"}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="shared-event-actions">
                    <button 
                      className="shared-event-remove"
                      onClick={() => handleRemoveEvent(event.id, event.robotId)}
                      aria-label="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="shared-event-content">
                    <div className="shared-event-columns">
                      {/* 左侧图片和状态 */}
                      <div className="shared-event-media">
                        <div className="shared-event-image-container">
                          <img 
                            src={event.imagePath || `/api/placeholder/400/300?text=Unit ${event.robotId}`}
                            className="shared-event-image"
                            alt={event.event || "Event"}
                          />
                        </div>
                        
                        {/* Add Check Details button */}
                        <div className="event-check-details-container">
                          <button 
                            className="event-check-details-btn"
                            onClick={(e) => handleGoDetailsClick(event, e)}
                            title="Go to video and map view"
                          >
                            <ExternalLink size={14} />
                            <span>Check Details</span>
                          </button>
                        </div>
                        
                        {/* 状态部分改进 */}
                        <div className="event-status-section">
                          <h4 className="status-section-title">Status</h4>
                          
                          {/* 当前状态显示 */}
                          <div className="current-status">
                            <div className={`status-badge status-${event.status || 'pending'}`}>
                              {event.status === 'pending' && 'Pending Review'}
                              {event.status === 'in-progress' && 'In Progress'}
                              {event.status === 'investigation' && 'Under Investigation'}
                              {event.status === 'resolved' && 'Resolved'}
                              {event.status === 'closed' && 'Closed'}
                            </div>
                            
                            {/* 当前行动要求显示 */}
                            {event.actionRequired && (
                              <div className={`action-badge action-${event.actionRequired}`}>
                                {actionRequiredLabels[event.actionRequired] || event.actionRequired}
                              </div>
                            )}
                          </div>
                          
                          {/* 状态变更下拉菜单 */}
                          <div className="status-update-section">
                            <label htmlFor={`status-select-${eventId}`} className="status-label">Update Status:</label>
                            <select 
                              id={`status-select-${eventId}`}
                              className="status-select"
                              value={event.status || 'pending'}
                              onChange={(e) => {
                                // 更新事件状态
                                updateEventStatus(eventId, e.target.value);
                              }}
                            >
                              <option value="pending">Pending Review</option>
                              <option value="in-progress">In Progress</option>
                              <option value="investigation">Under Investigation</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* 单一响应按钮 */}
                        {/* <button
                          className="media-action-btn respond"
                          onClick={() => handleRespond(eventId)}
                        >
                          <MessageSquare size={16} />
                          Add Response
                        </button> */}
                      </div>
                      
                      {/* 右侧详情 */}
                      <div className="shared-event-details">
                        {/* 事件描述或初始消息 */}
                        {event.shareMessage && (
                          <div className="shared-event-message">
                            <div className="message-header">
                              <span className="message-label">Initial Report:</span>
                            </div>
                            <p className="message-content">{event.shareMessage}</p>
                          </div>
                        )}
                        
                        {/* 消息部分 */}
                        {hasMessages && (
                          <div className="shared-event-messages">
                            <h4 className="shared-event-section-title">
                              <MessageSquare size={14} />
                              Team Communication
                            </h4>
                            
                            <div className="shared-event-messages-list">
                              {event.messages.map(msg => (
                                <div 
                                  key={msg.id}
                                  className="shared-message"
                                >
                                  <div className="shared-message-header">
                                    <div className="shared-message-sender">
                                      {msg.sender}
                                    </div>
                                    <div className="shared-message-time">
                                      {formatTime(msg.timestamp)}
                                    </div>
                                  </div>
                                  <div className="shared-message-content">
                                    {msg.message}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* 快速响应输入框 */}
                            <div className="shared-event-message-input">
                              <input
                                type="text"
                                placeholder="Type a response and press Enter to send..."
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && e.target.value.trim()) {
                                    // 设置当前响应数据
                                    setCurrentResponseId(eventId);
                                    setCurrentResponse(e.target.value.trim());
                                    sendResponse();
                                    e.target.value = '';
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* 备注部分 */}
                        {event.notes && event.notes.length > 0 && (
                          <div className="shared-event-notes">
                            <h4 className="shared-event-section-title">
                              <FileText size={14} />
                              Notes
                            </h4>
                            <div className="shared-event-notes-list">
                              {event.notes.map(note => (
                                <div 
                                  key={note.id}
                                  className={`shared-note ${note.isImportant ? 'important' : ''}`}
                                >
                                  <div 
                                    className="shared-note-content"
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="shared-space-pagination">
          <button 
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SharedSpace;